import React from "react";
import { Flyout } from "../../../components/global_components/Flyout";
import { Button, Icon } from "semantic-ui-react";
import { useForm } from "react-hook-form";
import { FormDataFilter } from "../../schemas/DemoTypes";
import { ListFieldDefinition } from "../../queries/getCrmListFieldDefinitions";
import { DataFilterGroupType, DataFilterType } from "../../queries/getCrmRequests";
import { GetLookUpOptionsParamsType, LookUpOptionsType } from "../../queries/getCrmLookUpOptions";
import { yupResolver } from "@hookform/resolvers";
import { FilterGroupResolver } from "../../schemas/FilterGroupResolver";
import { FilterGroupForm } from "./FilterGroupForm";
import { getPicklistOptions } from "../../utils/getPicklistOptions";

const convertDataFilterToFormFilter = (
  filter: DataFilterType,
  field: ListFieldDefinition,
  fieldName: string,
): FormDataFilter => {
  switch (filter.type) {
    case "Address":
    case "Text":
    case "TextArea":
    case "Email":
    case "PhoneNumber":
    case "URL":
      if (filter.stringCondition === "Empty" || filter.stringCondition === "NotEmpty") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: [],
            stringCondition: filter.stringCondition,
          },
        };
      }
      if ("stringValue" in filter && filter.stringValue) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: [filter.stringValue],
            stringCondition: filter.stringCondition,
          },
        };
      }
      if ("jsonValue" in filter && filter.jsonValue && filter.jsonValue.length > 0) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: filter.jsonValue.map((value) => String(value)),
            stringCondition: filter.stringCondition,
          },
        };
      }
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          jsonValue: [],
          stringCondition: filter.stringCondition,
        },
      };
    case "Date":
    case "DateTime":
    case "Time":
      if (filter.dateCondition === "Empty" || filter.dateCondition === "NotEmpty") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            dateValue: {
              startDate: undefined,
              endDate: undefined,
              dateOption: undefined,
            },
            dateCondition: filter.dateCondition,
          },
        };
      }
      if ("stringValue" in filter && filter.stringValue) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            dateValue: {
              startDate: undefined,
              endDate: undefined,
              dateOption: filter.stringValue,
            },
            dateCondition: filter.dateCondition,
          },
        };
      }
      if ("startDateValue" in filter && "endDateValue" in filter) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            dateValue: {
              startDate: !!filter.startDateValue ? new Date(filter.startDateValue) : undefined,
              endDate: !!filter.endDateValue ? new Date(filter.endDateValue) : undefined,
              dateOption: undefined,
            },
            dateCondition: filter.dateCondition,
          },
        };
      }
      if ("startDateValue" in filter) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            dateValue: {
              startDate: !!filter.startDateValue ? new Date(filter.startDateValue) : undefined,
              endDate: undefined,
              dateOption: undefined,
            },
            dateCondition: filter.dateCondition,
          },
        };
      }
      if ("endDateValue" in filter) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            dateValue: {
              startDate: undefined,
              endDate: !!filter.endDateValue ? new Date(filter.endDateValue) : undefined,
              dateOption: undefined,
            },
            dateCondition: filter.dateCondition,
          },
        };
      }
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          dateValue: {
            startDate: undefined,
            endDate: undefined,
            dateOption: undefined,
          },
          dateCondition: filter.dateCondition,
        },
      };
    case "Number":
      if (filter.numberCondition === "Empty" || filter.numberCondition === "NotEmpty") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: undefined,
              secondNumber: undefined,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      if (filter.numberCondition === "Between") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: filter.integerValue,
              secondNumber: filter.secondIntegerValue,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      if ("integerValue" in filter) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: filter.integerValue,
              secondNumber: undefined,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          numberValue: {
            firstNumber: undefined,
            secondNumber: undefined,
          },
          numberCondition: filter.numberCondition,
        },
      };
    case "Percent":
    case "Currency":
      if (filter.numberCondition === "Empty" || filter.numberCondition === "NotEmpty") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: undefined,
              secondNumber: undefined,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      if (filter.numberCondition === "Between") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: filter.floatValue,
              secondNumber: filter.secondFloatValue,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      if ("floatValue" in filter) {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            numberValue: {
              firstNumber: filter.floatValue,
              secondNumber: undefined,
            },
            numberCondition: filter.numberCondition,
          },
        };
      }
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          numberValue: {
            firstNumber: undefined,
            secondNumber: undefined,
          },
          numberCondition: filter.numberCondition,
        },
      };
    case "Checkbox":
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          booleanCondition: filter.booleanCondition ?? "Equal",
          booleanValue: filter.booleanValue ?? true,
        },
      };
    case "LookUp":
      if (field.type !== "LookUp") {
        throw new Error(`Field type mismatch: expected LookUp, got ${field.type}`);
      }
      if (filter.dropdownCondition === "Empty" || filter.dropdownCondition === "NotEmpty") {
        return {
          lookUpType: field.lookUpType,
          lookUpNeedsSearch: field.lookUpNeedsSearch,
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: [],
            dropdownCondition: filter.dropdownCondition,
          },
        };
      }
      if ("jsonValue" in filter && filter.jsonValue && filter.jsonValue.length > 0) {
        return {
          lookUpType: field.lookUpType,
          lookUpNeedsSearch: field.lookUpNeedsSearch,
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: filter.jsonValue.map((value) => String(value)),
            dropdownCondition: filter.dropdownCondition,
          },
        };
      }
      return {
        lookUpType: field.lookUpType,
        lookUpNeedsSearch: field.lookUpNeedsSearch,
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        filter: {
          jsonValue: [],
          dropdownCondition: filter.dropdownCondition,
        },
      };

    case "DropdownMultiSelect":
    case "Dropdown":
      if (field.type !== "Dropdown" && field.type !== "DropdownMultiSelect") {
        throw new Error(`Field type mismatch: expected Dropdown or DropdownMultiSelect, got ${field.type}`);
      }
      if (filter.dropdownCondition === "Empty" || filter.dropdownCondition === "NotEmpty") {
        return {
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          picklist: field.picklist ? getPicklistOptions(field.picklist) : [],
          filter: {
            jsonValue: [],
            dropdownCondition: filter.dropdownCondition,
          },
        };
      }
      if ("jsonValue" in filter && filter.jsonValue && filter.jsonValue.length > 0) {
        return {
          picklist: field.picklist ? getPicklistOptions(field.picklist) : [],
          type: filter.type,
          field: fieldName,
          fieldId: field.fieldId,
          fieldTable: field.fieldTable,
          label: field.label,
          filter: {
            jsonValue: filter.jsonValue.map((value) => String(value)),
            dropdownCondition: filter.dropdownCondition,
          },
        };
      }
      return {
        type: filter.type,
        field: fieldName,
        fieldId: field.fieldId,
        fieldTable: field.fieldTable,
        label: field.label,
        picklist: field.picklist ? getPicklistOptions(field.picklist) : [],
        filter: {
          jsonValue: [],
          dropdownCondition: filter.dropdownCondition,
        },
      };
  }
};

const convertFormFieldFilterToFieldFilter = (filter: FormDataFilter, order: number): DataFilterType => {
  switch (filter.type) {
    case "Address":
    case "Text":
    case "TextArea":
    case "Email":
    case "PhoneNumber":
    case "URL":
      if (filter.filter.stringCondition === "Empty" || filter.filter.stringCondition === "NotEmpty") {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          stringCondition: filter.filter.stringCondition,
        };
      }
      const stringValue = filter.filter.jsonValue?.length === 1 ? filter.filter.jsonValue[0] : undefined;
      if (stringValue) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          stringCondition: filter.filter.stringCondition,
          stringValue: stringValue,
        };
      }
      const jsonStringValues = filter.filter.jsonValue?.length ? filter.filter.jsonValue : [];
      if (jsonStringValues?.length) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          stringCondition: filter.filter.stringCondition,
          jsonValue: jsonStringValues.map((value) => String(value)),
        };
      }
      throw new Error("At least one value is required for string filters");
    case "Number":
      if (filter.filter.numberCondition === "Empty" || filter.filter.numberCondition === "NotEmpty") {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          numberCondition: filter.filter.numberCondition,
        };
      }
      const { firstNumber, secondNumber } = filter.filter.numberValue;
      if (filter.filter.numberCondition === "Between") {
        if (firstNumber === undefined || secondNumber === undefined) {
          throw new Error("Both integer values are required for 'Between' condition");
        }
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          numberCondition: filter.filter.numberCondition,
          integerValue: firstNumber,
          secondIntegerValue: secondNumber,
        };
      }
      if (firstNumber === undefined) {
        throw new Error("First integer value is required");
      }
      return {
        type: filter.type,
        order,
        field: filter.field,
        fieldId: filter.fieldId,
        fieldTable: filter.fieldTable,
        numberCondition: filter.filter.numberCondition,
        integerValue: firstNumber,
      };
    case "Percent":
    case "Currency":
      if (filter.filter.numberCondition === "Empty" || filter.filter.numberCondition === "NotEmpty") {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          numberCondition: filter.filter.numberCondition,
        };
      }
      const { firstNumber: firstFloatValue, secondNumber: secondFloatValue } = filter.filter.numberValue;
      if (filter.filter.numberCondition === "Between") {
        if (firstFloatValue === undefined || secondFloatValue === undefined) {
          throw new Error("Both integer values are required for 'Between' condition");
        }
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          numberCondition: filter.filter.numberCondition,
          floatValue: firstFloatValue,
          secondFloatValue: secondFloatValue,
        };
      }
      if (firstFloatValue === undefined) {
        throw new Error("First float value is required");
      }
      return {
        type: filter.type,
        order,
        field: filter.field,
        fieldId: filter.fieldId,
        fieldTable: filter.fieldTable,
        numberCondition: filter.filter.numberCondition,
        floatValue: firstFloatValue,
      };
    case "Checkbox":
      return {
        type: filter.type,
        order,
        field: filter.field,
        fieldId: filter.fieldId,
        fieldTable: filter.fieldTable,
        booleanValue: filter.filter.booleanValue,
        booleanCondition: filter.filter.booleanCondition,
      };

    case "Date":
    case "DateTime":
    case "Time":
      if (filter.filter.dateCondition === "Empty" || filter.filter.dateCondition === "NotEmpty") {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dateCondition: filter.filter.dateCondition,
        };
      }
      const { startDate, endDate, dateOption } = filter.filter.dateValue;
      if (dateOption) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dateCondition: filter.filter.dateCondition,
          stringValue: dateOption,
        };
      }
      if (startDate && endDate) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dateCondition: filter.filter.dateCondition,
          startDateValue: startDate.toISOString(),
          endDateValue: endDate.toISOString(),
        };
      }
      if (startDate) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dateCondition: filter.filter.dateCondition,
          startDateValue: startDate.toISOString(),
        };
      }
      if (endDate) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dateCondition: filter.filter.dateCondition,
          endDateValue: endDate.toISOString(),
        };
      }
      throw new Error("At least one date value is required");
    case "LookUp":
    case "DropdownMultiSelect":
    case "Dropdown":
      if (filter.filter.dropdownCondition === "Empty" || filter.filter.dropdownCondition === "NotEmpty") {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dropdownCondition: filter.filter.dropdownCondition,
        };
      }
      const jsonValue = filter.filter.jsonValue?.length ? filter.filter.jsonValue : [];
      if (jsonValue?.length) {
        return {
          type: filter.type,
          order,
          field: filter.field,
          fieldId: filter.fieldId,
          fieldTable: filter.fieldTable,
          dropdownCondition: filter.filter.dropdownCondition,
          jsonValue: jsonValue.map((value) => value),
        };
      }
      throw new Error("At least one value is required for dropdown or lookup filters");
    default:
      throw new Error(`Unknown filter type`);
  }
};

export interface IFilterFlyoutProps {
  onClose: () => void;
  fieldDefinitions: Record<string, ListFieldDefinition>;
  filterGroup?: DataFilterGroupType;
  open?: boolean;
  onSubmit: (data: DataFilterGroupType) => void;
  onViewSave?: (data: DataFilterGroupType) => void;
  lookUpOptionsFunction: (
    params: GetLookUpOptionsParamsType,
    enabled: boolean,
  ) => {
    isLoading: boolean;
    data:
      | {
          options: LookUpOptionsType[];
        }
      | undefined;
  };
  shouldRenderSaveToViewButton?: boolean;
}

export function FilterFlyout({
  onClose,
  onSubmit,
  fieldDefinitions,
  filterGroup,
  open = false,
  lookUpOptionsFunction,
  onViewSave,
  shouldRenderSaveToViewButton,
}: IFilterFlyoutProps) {
  const defaultFilters = filterGroup
    ? filterGroup.filters.reduce((acc, filter) => {
        const field =
          fieldDefinitions[filter.field] || Object.values(fieldDefinitions).find((f) => f.fieldId === filter.fieldId);
        if (!field) {
          return acc;
        }
        return [...acc, convertDataFilterToFormFilter(filter, field, filter.field)];
      }, [] as FormDataFilter[])
    : [];

  const { handleSubmit, ...formContext } = useForm({
    defaultValues: {
      advancedLogic: filterGroup?.advancedLogic,
      useAdvancedLogic: !!filterGroup?.advancedLogic,
      filters: defaultFilters,
    },
    resolver: yupResolver(FilterGroupResolver),
    mode: "onSubmit",
    shouldUnregister: false,
  });

  const formatFormData = (data: {
    advancedLogic?: string | null;
    useAdvancedLogic: boolean;
    filters: FormDataFilter[];
  }): DataFilterGroupType => {
    const filterGroup: DataFilterGroupType = {
      advancedLogic: data.useAdvancedLogic ? data.advancedLogic : undefined,
      filters: data.filters.map((f, index) => convertFormFieldFilterToFieldFilter(f, index + 1)),
    };
    return filterGroup;
  };

  const handleOnSubmit = (data: {
    advancedLogic?: string | null;
    useAdvancedLogic: boolean;
    filters: FormDataFilter[];
  }) => {
    onSubmit(formatFormData(data));
    onClose();
  };

  const handleOnSaveToView = (data: {
    advancedLogic?: string | null;
    useAdvancedLogic: boolean;
    filters: FormDataFilter[];
  }) => {
    if (onViewSave) {
      onViewSave(formatFormData(data));
      onClose();
    }
  };

  return (
    <>
      {" "}
      <Flyout open={open} onClose={onClose} contain={false}>
        <Flyout.Header>
          <div className='tw-flex tw-items-center'>
            <Icon className='button-icon' name='close' size='large' onClick={onClose} />
            <div>
              <h3>Add Filters</h3>
            </div>
          </div>
          <div>
            <Button primary type='submit' onClick={handleSubmit(handleOnSubmit)}>
              {" "}
              Apply{" "}
            </Button>
            {shouldRenderSaveToViewButton && onViewSave && (
              <Button primary type='submit' onClick={handleSubmit(handleOnSaveToView)}>
                Save To View
              </Button>
            )}
          </div>
        </Flyout.Header>
        <div className='tw-h-full tw-overflow-scroll tw-bg-[#F5F7F8]'>
          <Flyout.Body>
            <FilterGroupForm
              {...formContext}
              fieldDefinitions={fieldDefinitions}
              lookUpOptionsFunction={lookUpOptionsFunction}
            />
          </Flyout.Body>
        </div>
      </Flyout>
    </>
  );
}
